import {
    CartItemInput,
    CartItemInterface,
    CustomizableOptionInterface,
    GiftCardProduct,
    ProductInterface,
    CustomizableFieldOption,
    CustomizableFileOption,
    CustomizableAreaOption,
    CustomizableDateOption,
    CustomizableDropDownOption,
    CustomizableMultipleOption,
    CustomizableRadioOption,
    CustomizableCheckboxOption,
    GiftCardCartItem as CoreGiftCardItem
} from '@pylot-data/pylotschema'
import { itemHasKey } from '@corratech/pylot-utils/src/deep-value'
import { processItemPrice } from './processItemPrice'

// Extend core GiftCardCartItem so that we can add attributes regardless of whether they are defined in the type
type GiftCardCartItem = CoreGiftCardItem &
    Record<string, string | number | null>

const isGiftCardCartItem = (
    item: CartItemInterface,
    product: ProductInterface
): item is GiftCardCartItem => {
    return isGiftCardProduct(product)
}

const isGiftCardProduct = (
    product: ProductInterface
): product is GiftCardProduct => {
    return 'gift_card_options' in product
}

type CustomizableSingleOption =
    | CustomizableFieldOption
    | CustomizableFileOption
    | CustomizableAreaOption
    | CustomizableDateOption

type CustomizableArrayOption =
    | CustomizableDropDownOption
    | CustomizableMultipleOption
    | CustomizableRadioOption
    | CustomizableCheckboxOption

const isCustomizableOption = (
    option: CustomizableOptionInterface
): option is CustomizableSingleOption | CustomizableArrayOption => {
    return itemHasKey(option, 'value')
}

const isCustomizableSingleOption = (
    option: CustomizableSingleOption | CustomizableArrayOption
): option is CustomizableSingleOption => {
    return !Array.isArray(option.value)
}

export const processGiftCardItem = (
    input: CartItemInput,
    item: CartItemInterface,
    product: ProductInterface
): void => {
    if (!isGiftCardProduct(product) || !isGiftCardCartItem(item, product)) {
        return
    }
    let price = 0
    if (input?.entered_options) {
        const enteredOptions: Record<string, string> = {}
        input.entered_options.forEach((option) => {
            if (!option) {
                return
            }
            enteredOptions[option.uid] = option.value
        })
        product.gift_card_options.forEach((GCOption) => {
            if (!GCOption || !isCustomizableOption(GCOption)) {
                return
            }
            if (isCustomizableSingleOption(GCOption)) {
                const optionValueUid = String(GCOption.value?.uid)
                if (!itemHasKey(enteredOptions, optionValueUid)) {
                    return
                }
                const optionCode = String(GCOption.title)
                    .toLowerCase()
                    .replace(/\s+/g, '_')
                // Gift card price as custom input
                if (optionCode === 'custom_giftcard_amount') {
                    price = Number(enteredOptions[optionValueUid])
                    return
                }
                item[optionCode] = enteredOptions[optionValueUid]
            } else {
                // TODO: implement logic for handling inputs with multiple values, once we have them
            }
        })
    }
    // Gift card price as dropdown
    if (
        product.giftcard_amounts &&
        Array.isArray(input?.selected_options) &&
        input?.selected_options.length > 0
    ) {
        const amountOptionUid = input.selected_options[0]
        product.giftcard_amounts.forEach((amount) => {
            if (amount !== null && amount.uid === amountOptionUid) {
                price = Number(amount.value)
            }
        })
    }
    processItemPrice(item, product.price_range.minimum_price, price)
    item['__typename'] = 'GiftCardCartItem'
}
